import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Plus, RotateCcw, Trash2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils/cn";
import {
  CHAT_POLL_MAX_OPTIONS,
  CHAT_POLL_MIN_OPTIONS,
  type IChatOutgoingShortcut,
  type IChatShortcutMetadata,
} from "@/types/(my-space)/chat.interface";
import { isValidHttpUrl } from "../_lib/chat-utils";
import { CHAT_SHORTCUT_ACTION_DEFS, CHAT_SHORTCUTS } from "../_lib/chat-shortcuts";
import { ShortcutItemsPanel } from "./ShortcutItemsPanel";

interface BaseProps {
  open: boolean;
  onClose: () => void;
}

// ── Poll ────────────────────────────────────────────────────────────────────

export const PollDialog = ({
  open,
  onClose,
  onSubmit,
}: BaseProps & {
  onSubmit: (poll: { question: string; options: string[]; allowMultiple: boolean }) => void;
}) => {
  const { t } = useTranslation("chat");
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuestion("");
    setOptions(["", ""]);
    setAllowMultiple(false);
    setError(null);
  }, [open]);

  const submit = () => {
    const q = question.trim();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!q) return setError(t("poll.question_required"));
    if (opts.length < CHAT_POLL_MIN_OPTIONS) return setError(t("poll.options_required"));
    if (new Set(opts.map((o) => o.toLowerCase())).size !== opts.length) {
      return setError(t("poll.options_unique"));
    }
    onSubmit({ question: q, options: opts, allowMultiple });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("poll.create")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="chat-poll-question">{t("poll.question")}</Label>
            <Input
              id="chat-poll-question"
              value={question}
              maxLength={300}
              onChange={(e) => setQuestion(e.target.value)}
              className="h-10"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            {options.map((value, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={value}
                  maxLength={200}
                  placeholder={t("poll.option", { number: i + 1 })}
                  aria-label={t("poll.option", { number: i + 1 })}
                  onChange={(e) =>
                    setOptions((list) => list.map((o, j) => (j === i ? e.target.value : o)))
                  }
                  className="h-10"
                />
                {options.length > CHAT_POLL_MIN_OPTIONS ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("poll.remove_option", { number: i + 1 })}
                    onClick={() => setOptions((list) => list.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
              </div>
            ))}
            {options.length < CHAT_POLL_MAX_OPTIONS ? (
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => setOptions((list) => [...list, ""])}
              >
                <Plus className="size-4" />
                {t("poll.add_option")}
              </Button>
            ) : null}
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            {t("poll.allow_multiple")}
            <Switch checked={allowMultiple} onCheckedChange={setAllowMultiple} />
          </label>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("composer.cancel")}
          </Button>
          <Button variant="primary" onClick={submit}>
            {t("composer.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Link ────────────────────────────────────────────────────────────────────

export const LinkDialog = ({
  open,
  onClose,
  onSubmit,
}: BaseProps & { onSubmit: (link: { url: string; title?: string }) => void }) => {
  const { t } = useTranslation("chat");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl("");
    setTitle("");
    setError(false);
  }, [open]);

  const submit = () => {
    const raw = url.trim();
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    if (!isValidHttpUrl(withScheme)) return setError(true);
    onSubmit({ url: withScheme, title: title.trim() || undefined });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("link.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5" dir="ltr">
            <Label htmlFor="chat-link-url" className="self-start rtl:self-end">
              {t("link.url")}
            </Label>
            <Input
              id="chat-link-url"
              type="url"
              value={url}
              aria-invalid={error}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              // eslint-disable-next-line i18next/no-literal-string -- URL format example
              placeholder="https://"
              className="h-10"
              autoFocus
            />
            {error ? <p className="text-sm text-danger">{t("link.invalid")}</p> : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="chat-link-title">{t("link.link_title")}</Label>
            <Input
              id="chat-link-title"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="h-10"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("composer.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={!url.trim()}>
            {t("composer.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Shortcut ────────────────────────────────────────────────────────────────

/** What the server is sent, and the metadata the optimistic bubble shows. */
export interface ShortcutPick {
  shortcut: IChatOutgoingShortcut;
  metadata: IChatShortcutMetadata;
}

type ShortcutTab = "pages" | "items" | "actions";
const SHORTCUT_TABS: ShortcutTab[] = ["pages", "items", "actions"];

const PickTile = ({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-center gap-2 rounded-md border border-border p-3 text-center text-sm text-foreground hover:border-primary hover:bg-primary/5"
  >
    <span className="flex size-10 items-center justify-center rounded-full bg-primary-muted text-primary">
      <Icon className="size-5" />
    </span>
    {label}
  </button>
);

export const ShortcutDialog = ({
  open,
  onClose,
  onSubmit,
  myUserId,
}: BaseProps & { onSubmit: (pick: ShortcutPick) => void; myUserId: number | undefined }) => {
  const { t } = useTranslation("chat");
  const [tab, setTab] = useState<ShortcutTab>("pages");

  useEffect(() => {
    if (open) setTab("pages");
  }, [open]);

  const pick = (p: ShortcutPick) => {
    onSubmit(p);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("shortcut.title")}</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as ShortcutTab)} className="flex min-h-0 min-w-0 flex-col gap-4">
          <TabsList aria-label={t("shortcut.title")} className="grid w-full grid-cols-3">
            {SHORTCUT_TABS.map((tb) => (
              <TabsTrigger key={tb} value={tb}>
                {t(`shortcut.tabs.${tb}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="pages" className="mt-0">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CHAT_SHORTCUTS.map((s) => (
                <PickTile
                  key={s.route}
                  icon={s.icon}
                  label={s.label(t)}
                  onClick={() => {
                    const shortcut = { route: s.route, label: s.label(t) };
                    pick({ shortcut, metadata: shortcut });
                  }}
                />
              ))}
            </div>
          </TabsContent>
          <TabsContent value="items" className="mt-0 min-h-0">
            <ShortcutItemsPanel
              onPick={(item) =>
                pick({
                  shortcut: { entity: { type: item.type, id: item.id } },
                  // The server stores the same snapshot; I am its owner.
                  metadata: { entity: { ...item, ownerId: myUserId ?? 0 } },
                })
              }
            />
          </TabsContent>
          <TabsContent value="actions" className="mt-0">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CHAT_SHORTCUT_ACTION_DEFS.map((a) => (
                <PickTile
                  key={a.action}
                  icon={a.icon}
                  label={a.label(t)}
                  onClick={() =>
                    pick({ shortcut: { action: a.action }, metadata: { action: a.action } })
                  }
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ── Ticket ──────────────────────────────────────────────────────────────────

/** A project ticket I can see, sent as a Shortcut card (its key, status, due). */
export const TicketDialog = ({
  open,
  onClose,
  onSubmit,
  myUserId,
}: BaseProps & { onSubmit: (pick: ShortcutPick) => void; myUserId: number | undefined }) => {
  const { t } = useTranslation("chat");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("ticket.title")}</DialogTitle>
        </DialogHeader>
        {/* A grid cell grows to its widest row unless told not to: long titles must truncate. */}
        <div className="flex min-h-0 min-w-0 flex-col">
          <ShortcutItemsPanel
            only="task"
            onPick={(item) => {
              onSubmit({
                shortcut: { entity: { type: item.type, id: item.id } },
                metadata: { entity: { ...item, ownerId: myUserId ?? 0 } },
              });
              onClose();
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Camera ──────────────────────────────────────────────────────────────────

/** Webcam capture; the photo joins the draft tray like any picked file. */
export const CameraDialog = ({
  open,
  onClose,
  onCapture,
}: BaseProps & { onCapture: (file: File) => void }) => {
  const { t } = useTranslation("chat");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || shot) return;
    let cancelled = false;
    setFailed(false);
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => !cancelled && setFailed(true));
    if (!navigator.mediaDevices?.getUserMedia) setFailed(true);
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
  }, [open, shot]);

  useEffect(() => {
    if (!open && shot) {
      URL.revokeObjectURL(shot.url);
      setShot(null);
    }
  }, [open, shot]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => blob && setShot({ blob, url: URL.createObjectURL(blob) }),
      "image/jpeg",
      0.9,
    );
  };

  const use = () => {
    if (!shot) return;
    onCapture(new File([shot.blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("camera.title")}</DialogTitle>
        </DialogHeader>
        <div className="relative overflow-hidden rounded-md bg-black">
          {failed ? (
            <p className="p-8 text-center text-sm text-background">{t("camera.unavailable")}</p>
          ) : shot ? (
            <img src={shot.url} alt="" className="w-full" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={cn("w-full -scale-x-100")}
            />
          )}
        </div>
        <DialogFooter>
          {shot ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  URL.revokeObjectURL(shot.url);
                  setShot(null);
                }}
              >
                <RotateCcw className="size-4" />
                {t("camera.retake")}
              </Button>
              <Button variant="primary" onClick={use}>
                {t("camera.use_photo")}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={capture} disabled={failed}>
              <Camera className="size-4" />
              {t("camera.capture")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
